import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  formatFinancialYearLabel,
  getCalendarMonthFromFySlot,
  getFinancialYearDropdownOptions,
  getFinancialYearMonthSequence,
} from "../utils/financialYear";
import { readMonthlySaleReportParams, monthlySaleReportQuery } from "../utils/monthlySaleReportNav";
import { EXPECTED_EXPENSE_COLUMNS, EXPENSE_WEEKS_PER_MONTH } from "../features/expenseDataImport/constants";
import { parseExpenseExcelFile, rowTranDateInMonth } from "../features/expenseDataImport/services/expenseExcelParser";
import {
  formatExpenseUploadError,
  loadExpenseImportMeta,
  saveExpenseImportChunks,
} from "../features/expenseDataImport/services/expenseChunksService";
import { rowMatchesDateRange } from "../features/salesDataImport/utils/dateParse";
import { buildMonthWeekMeta } from "../features/salesDataImport/utils/weekUtils";
import { weekDateRangeLabel } from "../utils/weeklySalesUpload";
import { salesImportBranchDisplayName } from "../features/salesDataImport/config/salesImportBranchIds";
import "./MonthlySaleReportPage.css";

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function ExpenseUploadPage() {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readMonthlySaleReportParams(searchParams, now);

  const [fyStartYear, setFyStartYear] = useState(initial.fyStartYear);
  const [fySlot, setFySlot] = useState(initial.fySlot);
  const [uploadMode, setUploadMode] = useState("monthly");
  const [weekOfMonth, setWeekOfMonth] = useState(1);
  const [savedMeta, setSavedMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [sheetName, setSheetName] = useState("");

  const { calendarYear: year, monthIndex } = getCalendarMonthFromFySlot(fyStartYear, fySlot);
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const fyMonthSequence = useMemo(() => getFinancialYearMonthSequence(fyStartYear), [fyStartYear]);
  const periodLabel = `${MONTH_OPTIONS[monthIndex]} ${year}`;

  const weekMeta = useMemo(
    () => buildMonthWeekMeta(year, monthIndex, weekOfMonth),
    [year, monthIndex, weekOfMonth]
  );

  const periodFilterLabel =
    uploadMode === "weekly" ? `Week ${weekOfMonth} (${weekMeta.fromDate} → ${weekMeta.toDate})` : periodLabel;

  useEffect(() => {
    setSearchParams(monthlySaleReportQuery(fyStartYear, fySlot, "Main"), { replace: true });
  }, [fyStartYear, fySlot, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoadingMeta(true);
    (async () => {
      try {
        const meta = await loadExpenseImportMeta(year, monthIndex);
        if (!cancelled) setSavedMeta(meta);
      } catch {
        if (!cancelled) setSavedMeta(null);
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, monthIndex]);

  const periodRows = useMemo(() => {
    if (uploadMode === "weekly") {
      return parsedRows.filter((r) => rowMatchesDateRange(r, weekMeta.fromDate, weekMeta.toDate));
    }
    return parsedRows.filter((r) => rowTranDateInMonth(r, year, monthIndex));
  }, [parsedRows, uploadMode, weekMeta.fromDate, weekMeta.toDate, year, monthIndex]);

  const invalidRows = useMemo(() => periodRows.filter((r) => r.valid === false), [periodRows]);
  const validRows = useMemo(() => periodRows.filter((r) => r.valid !== false), [periodRows]);
  const totalAmount = useMemo(
    () => validRows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [validRows]
  );

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      setParsing(true);
      setParsedRows([]);
      setFileName(file.name);
      setSheetName("");
      try {
        const result = await parseExpenseExcelFile(file);
        setParsedRows(result.rows);
        setSheetName(result.sheetName);
        const inPeriod =
          uploadMode === "weekly"
            ? result.rows.filter((r) => rowMatchesDateRange(r, weekMeta.fromDate, weekMeta.toDate))
            : result.rows.filter((r) => rowTranDateInMonth(r, year, monthIndex));
        toast.success(`Parsed ${result.rows.length} rows · ${inPeriod.length} in ${periodFilterLabel}`);
      } catch (e) {
        toast.error(e?.message || String(e));
        setFileName("");
      } finally {
        setParsing(false);
      }
    },
    [uploadMode, weekMeta.fromDate, weekMeta.toDate, year, monthIndex, periodFilterLabel]
  );

  const handleUpload = async () => {
    if (!validRows.length) {
      toast.error(`No valid rows for ${periodFilterLabel}.`);
      return;
    }
    if (uploadMode === "weekly" && savedMeta?.hasMonthlyData) {
      toast.error(
        "This month already has a full monthly upload. Re-upload monthly data or use monthly mode to replace it."
      );
      return;
    }
    setUploading(true);
    setProgress("Uploading…");
    const tId = toast.loading(`Saving expenses for ${periodFilterLabel}…`);
    try {
      let userEmail = "";
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        userEmail = u?.email || "";
      } catch {
        /* ignore */
      }
      const result = await saveExpenseImportChunks({
        year,
        monthIndex,
        rows: validRows,
        uploadedBy: userEmail,
        fileName,
        importBatchId: `${Date.now()}`,
        onProgress: setProgress,
        uploadMode,
        weekOfMonth: uploadMode === "weekly" ? weekOfMonth : null,
      });
      const meta = await loadExpenseImportMeta(year, monthIndex);
      setSavedMeta(meta);
      setParsedRows([]);
      setFileName("");
      const savedLabel =
        uploadMode === "weekly"
          ? `week ${result.weekOfMonth} (${periodFilterLabel})`
          : periodLabel;
      toast.success(`Saved ${result.rowCount} expense rows for ${savedLabel}.`, { id: tId });
    } catch (e) {
      toast.error(formatExpenseUploadError(e), { id: tId });
    } finally {
      setUploading(false);
      setProgress("");
    }
  };

  const previewRows = periodRows.slice(0, 100);

  return (
    <>
      <MenuBar />
      <HeaderBar />

      <main className="msr-upload-page msr-no-print">
        <header className="msr-upload-page-head">
          <div>
            <p className="msr-upload-page-kicker">Reports</p>
            <h1 className="msr-upload-page-title">Expense upload</h1>
            <p className="msr-upload-page-sub">
              Upload SQL expense export for <strong>{periodLabel}</strong> — as a{" "}
              <strong>full month</strong> or <strong>weekly</strong> slice (Week 1–5). Columns: Branch ID,
              Voucher Type Name, Voucher No, Tran Date, Party Name, Transaction Type, Amount.
            </p>
          </div>
          <div className="flex flex-col gap-2 items-end text-sm">
            <Link to={`/reports/expense-party-monthly?fy=${fyStartYear}`} className="msr-upload-page-back">
              Party monthly report →
            </Link>
            <Link
              to={`/reports/expense-transfer-monthly?${monthlySaleReportQuery(fyStartYear, fySlot, "Main")}`}
              className="msr-upload-page-back"
            >
              Transfer monthly report →
            </Link>
          </div>
        </header>

        <div className="msr-toolbar msr-upload-toolbar">
          <label className="msr-toolbar-label">
            Financial year{" "}
            <select
              className="msr-toolbar-select"
              value={fyStartYear}
              onChange={(e) => setFyStartYear(Number(e.target.value))}
            >
              {fySelectOptions.map(({ startYear, label }) => (
                <option key={startYear} value={startYear}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="msr-toolbar-label">
            Month{" "}
            <select
              className="msr-toolbar-select"
              value={fySlot}
              onChange={(e) => setFySlot(Number(e.target.value))}
            >
              {fyMonthSequence.map((s) => (
                <option key={`${s.calendarYear}-${s.monthIndex}`} value={s.fySlot}>
                  {MONTH_OPTIONS[s.monthIndex]} {s.calendarYear}
                </option>
              ))}
            </select>
          </label>
          <span className="msr-toolbar-fy-hint">{fyLabel} · April–March</span>
        </div>

        <section className="msr-precalc-panel mb-4">
          <h2 className="msr-precalc-title">Upload mode</h2>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="expense-upload-mode"
                checked={uploadMode === "monthly"}
                onChange={() => setUploadMode("monthly")}
              />
              <span>
                <strong>Monthly upload</strong> — full month ({periodLabel})
              </span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="expense-upload-mode"
                checked={uploadMode === "weekly"}
                onChange={() => setUploadMode("weekly")}
              />
              <span>
                <strong>Weekly upload</strong> — one week slice only
              </span>
            </label>
          </div>
          {uploadMode === "weekly" ? (
            <label className="msr-toolbar-label">
              Week{" "}
              <select
                className="msr-toolbar-select"
                value={weekOfMonth}
                onChange={(e) => setWeekOfMonth(Number(e.target.value))}
              >
                {Array.from({ length: EXPENSE_WEEKS_PER_MONTH }, (_, i) => i + 1).map((w) => (
                  <option key={w} value={w}>
                    Week {w} — {weekDateRangeLabel(year, monthIndex, w)} ({buildMonthWeekMeta(year, monthIndex, w).fromDate}{" "}
                    → {buildMonthWeekMeta(year, monthIndex, w).toDate})
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="text-xs text-slate-600 m-0">
              Replaces the entire month (all weekly slices are cleared when you save a monthly file).
            </p>
          )}
          {uploadMode === "weekly" && savedMeta?.hasMonthlyData ? (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 mb-0">
              This month already has a <strong>monthly</strong> upload. Weekly slices cannot be added until you
              replace the month with a new monthly file (or clear monthly data in Firestore).
            </p>
          ) : null}
        </section>

        {savedMeta?.hasMonthlyData || savedMeta?.hasWeeklyData ? (
          <section className="msr-precalc-panel mb-4">
            <h2 className="msr-precalc-title">Saved — {periodLabel}</h2>
            {savedMeta.hasMonthlyData && savedMeta.monthlyLog ? (
              <p className="msr-precalc-meta">
                <strong>Monthly:</strong> {savedMeta.monthlyLog.rowCount?.toLocaleString("en-IN")} rows
                {savedMeta.monthlyLog.uploadedAt
                  ? ` · ${new Date(savedMeta.monthlyLog.uploadedAt).toLocaleString("en-IN")}`
                  : savedMeta.lastUploadedAt
                    ? ` · ${new Date(savedMeta.lastUploadedAt).toLocaleString("en-IN")}`
                    : ""}
                {savedMeta.monthlyLog.uploadedBy || savedMeta.lastUploadedBy
                  ? ` · ${savedMeta.monthlyLog.uploadedBy || savedMeta.lastUploadedBy}`
                  : ""}
                {savedMeta.monthlyLog.fileName || savedMeta.lastFileName
                  ? ` · ${savedMeta.monthlyLog.fileName || savedMeta.lastFileName}`
                  : ""}
              </p>
            ) : null}
            {savedMeta.hasWeeklyData ? (
              <div className="text-sm text-slate-700 space-y-1">
                <p className="msr-precalc-meta m-0">
                  <strong>Weekly slices:</strong> {savedMeta.weeklyRowTotal?.toLocaleString("en-IN")} rows total
                </p>
                <ul className="text-xs text-slate-600 list-disc pl-5 m-0">
                  {Array.from({ length: EXPENSE_WEEKS_PER_MONTH }, (_, i) => i + 1)
                    .filter((w) => savedMeta.weeklyLogs?.[w])
                    .map((w) => {
                      const log = savedMeta.weeklyLogs[w];
                      return (
                        <li key={w}>
                          Week {w} ({weekDateRangeLabel(year, monthIndex, w)}):{" "}
                          {log.rowCount?.toLocaleString("en-IN")} rows
                          {log.uploadedAt
                            ? ` · ${new Date(log.uploadedAt).toLocaleString("en-IN")}`
                            : ""}
                          {log.fileName ? ` · ${log.fileName}` : ""}
                        </li>
                      );
                    })}
                </ul>
              </div>
            ) : null}
          </section>
        ) : loadingMeta ? (
          <p className="text-sm text-slate-500 mb-4">Checking saved data…</p>
        ) : (
          <p className="text-sm text-slate-500 mb-4">No expense data saved for {periodLabel} yet.</p>
        )}

        <section className="msr-precalc-panel mb-4">
          <h2 className="msr-precalc-title">
            Upload file — {uploadMode === "weekly" ? `Week ${weekOfMonth}` : "Monthly"}
          </h2>
          <p className="text-xs text-slate-600 mb-3">
            Expected headers: <strong>{EXPECTED_EXPENSE_COLUMNS.join(" · ")}</strong>
            {uploadMode === "weekly" ? (
              <>
                {" "}
                · Tran Date must fall in <strong>{weekMeta.fromDate}</strong> → <strong>{weekMeta.toDate}</strong>
              </>
            ) : null}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              disabled={parsing || uploading}
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="text-sm"
            />
            {fileName ? (
              <span className="text-sm text-slate-600">
                {fileName}
                {sheetName ? ` (${sheetName})` : ""}
              </span>
            ) : null}
            <button
              type="button"
              className="msr-print-btn"
              disabled={
                !validRows.length ||
                uploading ||
                parsing ||
                (uploadMode === "weekly" && savedMeta?.hasMonthlyData)
              }
              onClick={handleUpload}
            >
              {uploading ? "Saving…" : `Save ${validRows.length || 0} rows`}
            </button>
          </div>
          {progress ? <p className="text-xs text-slate-500 mt-2">{progress}</p> : null}
          {parsedRows.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <span>
                In {uploadMode === "weekly" ? "week" : "month"}: <strong>{periodRows.length}</strong>
              </span>
              <span>
                Valid: <strong className="text-emerald-800">{validRows.length}</strong>
              </span>
              {invalidRows.length ? (
                <span>
                  Invalid: <strong className="text-red-700">{invalidRows.length}</strong>
                </span>
              ) : null}
              <span>
                Total amount: <strong>{fmt(totalAmount)}</strong>
              </span>
            </div>
          ) : null}
        </section>

        {previewRows.length > 0 ? (
          <section className="msr-precalc-panel">
            <h2 className="msr-precalc-title">
              Preview ({previewRows.length}
              {periodRows.length > previewRows.length ? ` of ${periodRows.length}` : ""})
            </h2>
            <div className="msr-precalc-table-wrap">
              <table className="msr-precalc-table text-xs">
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th>Voucher type</th>
                    <th>Voucher no</th>
                    <th className="msr-num">Tran date</th>
                    <th>Party</th>
                    <th>Txn type</th>
                    <th className="msr-num">Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r, i) => (
                    <tr key={`${r.voucherNo}-${r.tranDate}-${i}`} className={r.valid === false ? "bg-red-50" : ""}>
                      <td>{salesImportBranchDisplayName(r.branchId) || r.branchId || "—"}</td>
                      <td>{r.voucherTypeName || "—"}</td>
                      <td>{r.voucherNo || "—"}</td>
                      <td className="msr-num">{r.tranDate || "—"}</td>
                      <td>{r.partyName || "—"}</td>
                      <td>{r.transactionType || "—"}</td>
                      <td className="msr-num">{fmt(r.amount)}</td>
                      <td className={r.valid === false ? "text-red-700" : "text-emerald-700"}>
                        {r.valid === false ? (r.errors || []).join("; ") : "OK"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        <p className="text-xs text-slate-500 mt-6">
          FY {fyLabel} · Reports use the monthly file when present; otherwise they combine weekly slices W1–W5.
        </p>
      </main>
    </>
  );
}
